/// <reference types="node" />
import { scriptLocation } from "./scriptlocation";
/**
 * configuration object available to widgets.
 */
export class WidgetConfiguration {
    constructor(widgetTag, globalConfig, configOptions) {
        var _a, _b, _c;
        this.widgetTag = widgetTag;
        this.globalConfig = globalConfig;
        this.configOptions = configOptions;
        /**
         * basic log function that widgets should use instead of `console.log`
         */
        this.log = (message, ...optionalParams) => {
            this.internalLog("info", message, ...optionalParams);
        };
        /**
         * log a message at the debug level.
         */
        this.debug = (message, ...optionalParams) => {
            this.internalLog("debug", message, ...optionalParams);
        };
        /**
         * log a message at the info level.
         */
        this.info = (message, ...optionalParams) => {
            this.internalLog("info", message, ...optionalParams);
        };
        /**
         * log a message at the warn level.
         */
        this.warn = (message, ...optionalParams) => {
            this.internalLog("warn", message, ...optionalParams);
        };
        /**
         * log a message at the error level.
         */
        this.error = (message, ...optionalParams) => {
            this.internalLog("error", message, ...optionalParams);
        };
        this.environment =
            ((_c = (_b = (_a = globalConfig) === null || _a === void 0 ? void 0 : _a.widgets) === null || _b === void 0 ? void 0 : _b[widgetTag]) === null || _c === void 0 ? void 0 : _c.environment) ||
                globalConfig.environment;
        this.sharedCssUrls = globalConfig.sharedCssUrls || [];
    }
    /**
     * `fetch` function that widgets should use to allow the application
     * to intercept/log network calls.
     */
    get fetch() {
        return this.globalConfig.fetch;
    }
    /**
     * An instance of Bus that can be used to communicate with other widgets.
     */
    get bus() {
        return this.globalConfig.bus;
    }
    /**
     * Should this widget use ShadowDOM if available.
     * This is based on the browser support and the configuration options `neverUseShadowDOM`.
     */
    get useShadowDOM() {
        var _a;
        const configNeverUseShadowDOM = (_a = this.configOptions.neverUseShadowDOM, (_a !== null && _a !== void 0 ? _a : false));
        return this.globalConfig.shadowDomSupported && !configNeverUseShadowDOM;
    }
    /**
     * If true, the shared css (defined by the property sharedCssUrls) are injected.
     */
    get useGlobalCss() {
        var _a;
        const configNeverUseGlobalCss = (_a = this.configOptions.neverInjectGlobalCss, (_a !== null && _a !== void 0 ? _a : false));
        return this.useShadowDOM && !configNeverUseGlobalCss;
    }
    myConfiguration() {
        this.myConfigurationPromise =
            this.myConfigurationPromise ||
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                (async () => {
                    var _a, _b, _c;
                    if ((_c = (_b = (_a = this.globalConfig) === null || _a === void 0 ? void 0 : _a.widgets) === null || _b === void 0 ? void 0 : _b[this.widgetTag]) === null || _c === void 0 ? void 0 : _c.configuration) {
                        return this.globalConfig.widgets[this.widgetTag].configuration;
                    }
                    try {
                        const location = scriptLocation(this.widgetTag);
                        const configLocation = `${location.path}${this.widgetTag}.${this.environment}.json`;
                        const response = await this.fetch(configLocation, {
                            credentials: this.configOptions
                                .includeCredentialsWhenFetchingConfiguration
                                ? "include"
                                : "omit",
                            headers: new Headers({ Accept: "application/json" }),
                            method: "GET",
                        });
                        if (!response.ok) {
                            return undefined;
                        }
                        else {
                            const configurationData = await response.json();
                            return configurationData;
                        }
                    }
                    catch (e) {
                        this.error("Could not load configuration", e);
                        return undefined;
                    }
                })();
        return this.myConfigurationPromise;
    }
    /**
     * Internal functions used for logging.
     *
     * If no custom logging function has been given, it will use the
     * various console.xxx methods.
     */
    internalLog(level, message, ...optionalParams) {
        if (this.globalConfig.log) {
            this.globalConfig.log(this.widgetTag, level, message, ...optionalParams);
        }
        else {
            console.log(this.widgetTag, level, message, ...optionalParams);
        }
    }
}
